import $fetch from 'lib/fetch';
import qs from 'qs';
import { API_BASE_URL, parseResponseData } from '../../utils';
import {
  EnableWifiCallingParams,
  GetActivityLogsParams,
  GetAllSimTagsByTypeParams,
  GetFilteredSimsParams,
  UpdateSimNameParams,
  UpdateSimTagsParams,
} from './sim.requests.types';
import simResponseSchemas from './sim.responses.schemas';

const BASE_SERVICE_URL = `${API_BASE_URL}/sim` as const;

export async function getSimCardDetail(params: { iccid: string }) {
  const { iccid } = params;

  const response = await $fetch(`${BASE_SERVICE_URL}/${iccid}/sim-card-detail`);

  const data = parseResponseData(response, simResponseSchemas.getSimCardDetail);

  return data;
}

export async function getActivePortInRequestData(params: { iccid: string }) {
  const { iccid } = params;

  const response = await $fetch(
    `${API_BASE_URL}/${iccid}/active-portin-req-data`
  );

  const data = parseResponseData(
    response,
    simResponseSchemas.getActivePortInRequestData
  );

  return data;
}

export async function getAccountNumber(params: { iccid: string }) {
  const { iccid } = params;

  const response = await $fetch(`${BASE_SERVICE_URL}/${iccid}/account-number`);

  const data = parseResponseData(response, simResponseSchemas.getAccountNumber);

  return data;
}

export async function changePhoneNumber(
  body: { id: number; zipcode: string },
  ultraUserId: string
) {
  const response = await $fetch(`${BASE_SERVICE_URL}/change-phone-number`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { entityId: ultraUserId },
  });

  const data = parseResponseData(
    response,
    simResponseSchemas.changePhoneNumber
  );

  return data;
}

export async function getFilteredSims(params: GetFilteredSimsParams) {
  // remove empty strings from keyword array
  params.keyword = Array.isArray(params.keyword)
    ? params.keyword.filter((k) => k.trim()).filter(Boolean)
    : params.keyword;

  const response = await $fetch(`${BASE_SERVICE_URL}/filtered`, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  const data = parseResponseData(response, simResponseSchemas.getFilteredSims);

  return data;
}

export async function updateSimName(params: UpdateSimNameParams) {
  const response = await $fetch(`${BASE_SERVICE_URL}/updatesimname`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });

  const data = parseResponseData(response, simResponseSchemas.updateSimName);

  return data;
}

export async function updateSimTags({ simIdsWithTags }: UpdateSimTagsParams) {
  const response = await $fetch(`${BASE_SERVICE_URL}/updatetags`, {
    method: 'POST',
    body: JSON.stringify({
      sims: simIdsWithTags,
    }),
  });

  const data = parseResponseData(response, simResponseSchemas.updateSimTags);

  return data;
}

export async function getAllSimTags(params: GetAllSimTagsByTypeParams) {
  const { simType, simTab = 'all', searchQuery = '' } = params;

  const response = await $fetch(
    `${BASE_SERVICE_URL}/tags/${simTab}?simType=${simType}&q=${searchQuery}`
  );

  const data = parseResponseData(response, simResponseSchemas.getAllSimTags);

  return data;
}

export async function deleteSims(params: { ids: number[] }) {
  const response = await $fetch(`${BASE_SERVICE_URL}/delete`, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  const data = parseResponseData(response, simResponseSchemas.deleteSims);

  return data;
}

export async function generatePDF(params: { ICCID: string }) {
  const { ICCID } = params;

  const response = await $fetch(`${BASE_SERVICE_URL}/${ICCID}/generate-pdf`);

  const data = parseResponseData(response, simResponseSchemas.generatePDF);

  return data;
}

export async function downloadEsimPDF(params: { ICCID: string }) {
  const { ICCID } = params;

  const response = await $fetch(`${BASE_SERVICE_URL}/esim/${ICCID}/pdf`, {
    responseType: 'blob',
  });

  return response.data;
}

export async function getWifiCallingDetails(params: { iccid: string }) {
  const { iccid } = params;

  const response = await $fetch(
    `${BASE_SERVICE_URL}/subscriber/${iccid}/wificalling`
  );

  const data = parseResponseData(response, simResponseSchemas.getWifiCalling);

  // in case of sim ICCID is not found on partner api side
  if ('error' in data) throw data;

  return data;
}

export async function enableWifiCalling(params: EnableWifiCallingParams) {
  const response = await $fetch(`${BASE_SERVICE_URL}/enable-wifi-calling`, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  const data = parseResponseData(
    response,
    simResponseSchemas.enableWifiCalling
  );

  return data;
}

export async function getActivityLogs(params: GetActivityLogsParams) {
  const { iccid, limit, offset } = params;

  const q = qs.stringify({ limit, offset });

  const response = await $fetch(
    `${BASE_SERVICE_URL}/${iccid}/activity-logs?${q}`
  );

  const data = parseResponseData(response, simResponseSchemas.getActivityLogs);

  return data;
}

export async function getESimsQuantity() {
  const response = await $fetch(`${BASE_SERVICE_URL}/get-esim-quantity`);

  const data = parseResponseData(response, simResponseSchemas.getESimsQuantity);

  return data;
}
